import type { CurrencyCatalog } from "./CurrencyCatalogTypes";
import { BootDataError } from "../boot/retry/BootDataError";
import { BOOT_REQUEST_TIMEOUT_MS } from "../boot/retry/BootRequestTimeout";
import { BootHttpError } from "../boot/retry/BootHttpError";

export class CurrencyCatalogLoader {
  public async loadFromUrl(url: string): Promise<CurrencyCatalog> {

    if (url === "fixture") {
      const catalog = this.createFixtureCatalog();
      this.validateOrThrow(catalog);
      return catalog;
    }

    const res = await fetch(url, { method: "GET", signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
    if (!res.ok) throw await BootHttpError.of("CurrencyCatalogLoader", res);
    const json = (await res.json()) as unknown;

    // minimal shape check
    if (!json || typeof json !== "object") {
      throw new BootDataError("CurrencyCatalogLoader: invalid json payload (expected object)");
    }

    const catalog = json as CurrencyCatalog;
    this.validateOrThrow(catalog);
    return catalog;
  }

  private createFixtureCatalog(): CurrencyCatalog {
    // Minimal fixture: add more currencies as you need
    return {
      EUR: {
        currencyName: "EUR",
        currencySymbol: "€",
        spaceBetweenAmountAndSymbol: false,
        decimalDigits: 2,
      },
      AMD: {
        currencyName: "AMD",
        currencySymbol: "֏",
        spaceBetweenAmountAndSymbol: true,
        decimalDigits: 2,
      },
      USD: {
        currencyName: "USD",
        currencySymbol: "$",
        spaceBetweenAmountAndSymbol: false,
        decimalDigits: 2,
      },
      DEFAULT: {
        currencyName: "DEFAULT",
        currencySymbol: "",
        spaceBetweenAmountAndSymbol: true,
        decimalDigits: 2,
      },
    } as CurrencyCatalog;
  }

  private validateOrThrow(catalog: CurrencyCatalog): void {
    // Keep this intentionally light (your registry does deeper behavior anyway).
    // We just want to catch totally wrong payloads early.
    const entries = Object.entries(catalog);
    if (entries.length === 0) {
      throw new BootDataError("CurrencyCatalogLoader: catalog is empty");
    }

    for (const [code, v] of entries) {
      if (!v || typeof v !== "object") {
        throw new BootDataError(`CurrencyCatalogLoader: invalid entry for ${code} (expected object)`);
      }
      if (typeof v.currencySymbol !== "string") {
        throw new BootDataError(`CurrencyCatalogLoader: ${code}.currencySymbol must be a string`);
      }
      if (typeof v.decimalDigits !== "number" || !Number.isFinite(v.decimalDigits)) {
        throw new BootDataError(`CurrencyCatalogLoader: ${code}.decimalDigits must be a number`);
      }
      if (typeof v.spaceBetweenAmountAndSymbol !== "boolean") {
        throw new BootDataError(`CurrencyCatalogLoader: ${code}.spaceBetweenAmountAndSymbol must be boolean`);
      }
    }
  }



}